import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import api from '../../api/client';

interface CallPriority {
  id: number; code: number; name: string; color: string; sort_order: number;
}

export default function CallPrioritiesPage() {
  const [priorities, setPriorities] = useState<CallPriority[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CallPriority | null>(null);
  const [form, setForm] = useState({ code: 1, name: '', color: '#9e9e9e', sort_order: 0 });

  const load = () => api.get('/call-priorities').then((r) => setPriorities(r.data));
  useEffect(() => { load(); }, []);

  const handleOpen = (cp?: CallPriority) => {
    if (cp) {
      setEditing(cp);
      setForm({ code: cp.code, name: cp.name, color: cp.color, sort_order: cp.sort_order });
    } else {
      setEditing(null);
      setForm({ code: priorities.length + 1, name: '', color: '#9e9e9e', sort_order: priorities.length + 1 });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (editing) {
      await api.patch(`/call-priorities/${editing.id}`, form);
    } else {
      await api.post('/call-priorities', form);
    }
    setOpen(false);
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this priority level?')) return;
    await api.delete(`/call-priorities/${id}`);
    load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Call Priorities</Typography>
        <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add Priority</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Color</TableCell>
              <TableCell width={100} />
            </TableRow>
          </TableHead>
          <TableBody>
            {priorities.map((cp) => (
              <TableRow key={cp.id}>
                <TableCell>{cp.sort_order}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{cp.code}</TableCell>
                <TableCell>{cp.name}</TableCell>
                <TableCell><Chip size="small" label={cp.name} sx={{ bgcolor: cp.color, color: '#fff' }} /></TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(cp)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(cp.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Priority' : 'New Priority'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Code (numeric level)" type="number" margin="normal" value={form.code} onChange={(e) => setForm({ ...form, code: Number(e.target.value) })} slotProps={{ htmlInput: { min: 1 } }} />
          <TextField fullWidth label="Name" margin="normal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Emergency" />
          <TextField fullWidth label="Color" type="color" margin="normal" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          <TextField fullWidth label="Sort Order" type="number" margin="normal" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
